import { getIdToken } from './auth.service';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface OrganizationSettings {
  blockingEnabled: boolean;
  rateLimit: number;
  maxProductsPerOrg: number;
}

export interface OrganizationMember {
  userId: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
}

export interface Organization {
  _id: string;
  name: string;
  slug: string;
  retentionDays: number;
  settings: OrganizationSettings;
  members: OrganizationMember[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrganizationInput {
  name: string;
  slug?: string;
}

export interface UpdateOrganizationInput {
  name?: string;
  retentionDays?: number;
  settings?: Partial<OrganizationSettings>;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

async function handleResponse<T>(response: Response): Promise<T> {
  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json.error || json.message || `Request failed with status ${response.status}`);
  }

  // Backend returns { success, data } format
  if ('data' in json) {
    return json.data as T;
  }

  return json as T;
}

export async function listOrganizations(): Promise<Organization[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/organizations`, {
    method: 'GET',
    headers,
  });
  return handleResponse<Organization[]>(response);
}

export async function getOrganization(id: string): Promise<Organization> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/organizations/${id}`, {
    method: 'GET',
    headers,
  });
  return handleResponse<Organization>(response);
}

export async function createOrganization(input: CreateOrganizationInput): Promise<Organization> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/organizations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  return handleResponse<Organization>(response);
}

export async function updateOrganization(
  id: string,
  input: UpdateOrganizationInput
): Promise<Organization> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/organizations/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(input),
  });
  return handleResponse<Organization>(response);
}
